/**
 * On-screen touch controls: a floating virtual stick on the left half, jump / dash / use buttons
 * on the right, and an exit button (also clickable with the mouse).
 */
import type { Action, TouchDevice } from './input.ts';

interface Circle {
  x: number;
  y: number;
  r: number;
}

interface Btn extends Circle {
  action: Action;
  label: string;
}

type Role = { kind: 'stick'; ox: number; oy: number; x: number; y: number } | { kind: 'button'; action: Action } | { kind: 'exit' } | { kind: 'sound' };

export interface SoundToggle {
  muted(): boolean;
  toggle(): void;
}

export class TouchControls {
  /** shown after the first touch (or on coarse-pointer devices) */
  visible: boolean;
  private roles = new Map<number, Role>();
  private buttons: Btn[] = [];
  private exitBtn: Circle = { x: 0, y: 0, r: 0 };
  private soundBtn: Circle = { x: 0, y: 0, r: 0 };
  private stickR = 60;
  private w = 0;
  private h = 0;

  constructor(private device: TouchDevice, private onExit: () => void, private sound: SoundToggle) {
    this.visible = typeof matchMedia === 'function' && matchMedia('(pointer: coarse)').matches;
  }

  /** layout in CSS pixels */
  layout(w: number, h: number) {
    this.w = w;
    this.h = h;
    const u = Math.min(w, h);
    const big = Math.max(34, Math.min(70, u * 0.095));
    const mid = big * 0.8;
    const small = big * 0.62;
    const m = big * 0.45;
    const jump = { x: w - m - big, y: h - m - big };
    this.buttons = [
      { action: 'jump', label: 'JUMP', x: jump.x, y: jump.y, r: big },
      { action: 'dash', label: 'DASH', x: jump.x - big - mid - m * 0.6, y: jump.y + big - mid, r: mid },
      { action: 'interact', label: 'USE', x: jump.x + big - small, y: jump.y - big - small - m * 0.6, r: small },
      { action: 'pause', label: 'II', x: 26, y: 26, r: small * 0.7 },
    ];
    this.stickR = Math.max(40, Math.min(85, u * 0.12));
    this.exitBtn = { x: w - 26, y: 26, r: 18 };
    this.soundBtn = { x: w - 72, y: 26, r: 18 };
  }

  /** returns true when the event was consumed */
  pointerDown(id: number, x: number, y: number, touch: boolean): boolean {
    if (hit(this.exitBtn, x, y, 1.25) && (touch || this.visible)) {
      this.roles.set(id, { kind: 'exit' });
      return true;
    }
    if (hit(this.soundBtn, x, y, 1.25) && (touch || this.visible)) {
      this.roles.set(id, { kind: 'sound' });
      return true;
    }
    if (!touch) return false;
    this.visible = true;
    this.device.touched();
    let best: Btn | null = null;
    let bestD = Infinity;
    for (const b of this.buttons) {
      const d = Math.hypot(b.x - x, b.y - y) / b.r;
      if (d < 1.45 && d < bestD) {
        best = b;
        bestD = d;
      }
    }
    if (best) {
      this.roles.set(id, { kind: 'button', action: best.action });
      this.device.button(best.action, true);
      return true;
    }
    if (x < this.w * 0.5) {
      this.roles.set(id, { kind: 'stick', ox: x, oy: y, x, y });
      this.updateStick();
      return true;
    }
    return true;
  }

  pointerMove(id: number, x: number, y: number) {
    const r = this.roles.get(id);
    if (r?.kind !== 'stick') return;
    // the base follows the thumb when it is dragged far past the rim
    const dx = x - r.ox, dy = y - r.oy, d = Math.hypot(dx, dy), max = this.stickR * 1.5;
    if (d > max) {
      r.ox = x - (dx / d) * max;
      r.oy = y - (dy / d) * max;
    }
    r.x = x;
    r.y = y;
    this.updateStick();
  }

  pointerUp(id: number, x: number, y: number) {
    const r = this.roles.get(id);
    this.roles.delete(id);
    if (!r) return;
    if (r.kind === 'button' && ![...this.roles.values()].some((o) => o.kind === 'button' && o.action === r.action)) this.device.button(r.action, false);
    if (r.kind === 'stick') this.updateStick();
    if (r.kind === 'exit' && hit(this.exitBtn, x, y, 1.6)) this.onExit();
    if (r.kind === 'sound' && hit(this.soundBtn, x, y, 1.6)) this.sound.toggle();
  }

  releaseAll() {
    for (const [id] of this.roles) this.pointerUp(id, -1e9, -1e9);
  }

  private updateStick() {
    const r = [...this.roles.values()].find((o) => o.kind === 'stick') as Extract<Role, { kind: 'stick' }> | undefined;
    if (!r) {
      this.device.stick = { x: 0, y: 0 };
      return;
    }
    const dx = (r.x - r.ox) / this.stickR, dy = -(r.y - r.oy) / this.stickR;
    const d = Math.hypot(dx, dy);
    const dead = 0.22, full = 0.75;
    if (d < dead) {
      this.device.stick = { x: 0, y: 0 };
      return;
    }
    const mag = Math.min(1, (d - dead) / (full - dead));
    let x = (dx / d) * mag, y = (dy / d) * mag;
    // snap near-axis directions so running is full speed and crouch / look-up are clean
    if (Math.abs(y) < 0.4 * mag) {
      x = Math.sign(x) * mag;
      y = 0;
    } else if (Math.abs(x) < 0.4 * mag) {
      y = Math.sign(y) * mag;
      x = 0;
    }
    this.device.stick = { x, y };
  }

  /** draw in CSS pixel space (ctx already scaled by devicePixelRatio) */
  draw(ctx: CanvasRenderingContext2D) {
    ctx.save();
    if (this.visible) {
      const stick = [...this.roles.values()].find((o) => o.kind === 'stick') as Extract<Role, { kind: 'stick' }> | undefined;
      ctx.lineWidth = 2;
      if (stick) {
        ring(ctx, { x: stick.ox, y: stick.oy, r: this.stickR }, 'rgba(255,255,255,0.10)', 'rgba(255,255,255,0.35)');
        const dx = stick.x - stick.ox, dy = stick.y - stick.oy, d = Math.hypot(dx, dy), k = d > this.stickR ? this.stickR / d : 1;
        ring(ctx, { x: stick.ox + dx * k, y: stick.oy + dy * k, r: this.stickR * 0.45 }, 'rgba(255,255,255,0.35)', 'rgba(255,255,255,0.6)');
      } else {
        const hint = { x: this.stickR * 1.5, y: this.h - this.stickR * 1.5, r: this.stickR };
        ring(ctx, hint, 'rgba(255,255,255,0.05)', 'rgba(255,255,255,0.18)');
        ring(ctx, { ...hint, r: this.stickR * 0.45 }, 'rgba(255,255,255,0.12)', 'rgba(255,255,255,0.25)');
      }
      for (const b of this.buttons) {
        const on = this.device.isDown(b.action);
        ring(ctx, b, on ? 'rgba(255,255,255,0.38)' : 'rgba(255,255,255,0.13)', 'rgba(255,255,255,0.5)');
        ctx.fillStyle = 'rgba(255,255,255,0.85)';
        ctx.font = `600 ${Math.round(b.r * 0.42)}px system-ui, sans-serif`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(b.label, b.x, b.y + 1);
      }
      const e = this.exitBtn;
      ring(ctx, e, 'rgba(0,0,0,0.35)', 'rgba(255,255,255,0.6)');
      ctx.strokeStyle = 'rgba(255,255,255,0.9)';
      ctx.beginPath();
      const s = e.r * 0.42;
      ctx.moveTo(e.x - s, e.y - s);
      ctx.lineTo(e.x + s, e.y + s);
      ctx.moveTo(e.x + s, e.y - s);
      ctx.lineTo(e.x - s, e.y + s);
      ctx.stroke();
      this.drawSound(ctx);
    }
    ctx.restore();
  }

  private drawSound(ctx: CanvasRenderingContext2D) {
    const b = this.soundBtn, u = b.r / 18;
    ring(ctx, b, 'rgba(0,0,0,0.35)', 'rgba(255,255,255,0.6)');
    ctx.fillStyle = ctx.strokeStyle = 'rgba(255,255,255,0.9)';
    ctx.beginPath();
    ctx.moveTo(b.x - 8 * u, b.y - 3 * u);
    ctx.lineTo(b.x - 4 * u, b.y - 3 * u);
    ctx.lineTo(b.x + 1 * u, b.y - 7.5 * u);
    ctx.lineTo(b.x + 1 * u, b.y + 7.5 * u);
    ctx.lineTo(b.x - 4 * u, b.y + 3 * u);
    ctx.lineTo(b.x - 8 * u, b.y + 3 * u);
    ctx.closePath();
    ctx.fill();
    ctx.beginPath();
    if (this.sound.muted()) {
      ctx.moveTo(b.x + 4 * u, b.y - 4 * u);
      ctx.lineTo(b.x + 10 * u, b.y + 4 * u);
      ctx.moveTo(b.x + 10 * u, b.y - 4 * u);
      ctx.lineTo(b.x + 4 * u, b.y + 4 * u);
    } else {
      ctx.arc(b.x + 2 * u, b.y, 5 * u, -Math.PI / 4, Math.PI / 4);
      ctx.moveTo(b.x + 2 * u + 9 * u * Math.cos(-Math.PI / 4), b.y + 9 * u * Math.sin(-Math.PI / 4));
      ctx.arc(b.x + 2 * u, b.y, 9 * u, -Math.PI / 4, Math.PI / 4);
    }
    ctx.stroke();
  }
}

function hit(c: Circle, x: number, y: number, k = 1) {
  return Math.hypot(c.x - x, c.y - y) <= c.r * k;
}

function ring(ctx: CanvasRenderingContext2D, c: Circle, fill: string, stroke: string) {
  ctx.beginPath();
  ctx.arc(c.x, c.y, c.r, 0, Math.PI * 2);
  ctx.fillStyle = fill;
  ctx.fill();
  ctx.strokeStyle = stroke;
  ctx.stroke();
}
